<script setup lang="ts">
import type { Schemas } from "#shopware";
import {
  getSmallestThumbnailUrl,
  getTranslatedProperty,
} from "@shopware/helpers";

const props = defineProps<{
  cartItem: Schemas["LineItem"];
}>();

const { refreshCart, appliedPromotionCodes } = useCart();
const { cartItem } = toRefs(props);

const isLoading = ref(false);

const { trackRemoveFromCart } = useGtmEvents()
const { itemTotalPrice, isRemovable, removeItem, isProduct, itemOptions } =
  useCartItem(cartItem);

const removeCartItem = async () => {
  isLoading.value = true;
  await removeItem();

  trackRemoveFromCart(cartItem.value)

  await refreshCart();
  isLoading.value = false;
};

const listPrice: ComputedRef<number | undefined> = computed(() => {
  return cartItem.value?.price?.listPrice?.price;
});

const promotionForCartItem = computed(() => {
  let totalDiscount = 0;

  appliedPromotionCodes.value?.forEach((promotion) => {
    if (promotion.payload?.composition) {
      Object.values(promotion.payload.composition).forEach((item) => {
        if (item.id === cartItem.value?.id) {
          totalDiscount += item.discount;
        }
      });
    }
  });

  return totalDiscount;
});

const cartItemPriceObject = reactive({
  original: computed(() => {
    if (promotionForCartItem.value) {
      return getFormattedPrice(itemTotalPrice.value)
    } else if (listPrice.value) {
      return getFormattedPrice(listPrice.value)
    } else {
      return getFormattedPrice(itemTotalPrice.value)
    }
  }),
  discounted: computed(() => {
    if (promotionForCartItem.value) {
      return getFormattedPrice(itemTotalPrice.value - promotionForCartItem.value)
    } else if (listPrice.value) {
      return getFormattedPrice(itemTotalPrice.value)
    } else {
      return undefined
    }
  })
})
</script>

<template>
  <li
    v-if="isProduct"
    class="py-3 text-sm grid auto-cols-[80px_1fr_1fr_minmax(0,1fr)] grid-rows-[repeat(3,max-content)] gap-x-3 gap-y-2"
  >
    <img
      :src="getSmallestThumbnailUrl(cartItem.cover) || '/img/placeholder.png'"
      :alt="getTranslatedProperty(cartItem, 'label')"
      class="w-16 h-16 place-self-center row-span-full object-contain"
    />

    <div class="col-start-2 col-span-2">
      <span
        class="line-clamp-2 font-semibold"
        :title="getTranslatedProperty(cartItem, 'label')"
      >
        {{ getTranslatedProperty(cartItem, "label") }}
      </span>
    </div>

    <div class="flex flex-wrap gap-x-4 col-start-2 col-span-2">
      <span
        v-for="(option, optionIndex) in itemOptions"
        :key="optionIndex"
        class="text-xs font-medium text-gray-600"
      >
        {{ option.group }}:
        {{ option.option }}
      </span>
    </div>

    <div
      class="flex w-fit gap-x-1 self-start items-center col-start-2 col-span-1"
    >
      <Button
        v-if="isRemovable"
        text
        rounded
        size="small"
        icon="pi pi-trash"
        :pt="{
          root: 'hover:scale-110 transition',
        }"
        :aria-label="$t('checkout.items.removeButton')"
        :disabled="isLoading"
        @click="removeCartItem"
      />
    </div>

    <div v-if="itemTotalPrice" class="col-start-4 col-span-1 row-span-1 row-start-1 justify-self-end flex flex-col">
      <span
        :class="['text-gray-500', {'line-through': cartItemPriceObject.discounted}]"
      >
        {{ cartItemPriceObject.original }}
      </span>
  
      <span v-if="cartItemPriceObject.discounted" class="text-red-500">{{ cartItemPriceObject.discounted }}</span>
    </div>

    <div
      v-if="itemTotalPrice"
      class="col-start-3 col-span-2 row-start-3 justify-self-end self-center"
    >
      <ProductQuantityUpdateButton
        :cart-item="cartItem"
        input-class="w-10 h-4"
      />
    </div>
  </li>
</template>
